import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Activity, Shield, Brain, Smartphone, BarChart3, Package,
  ChevronRight, TrendingUp,
  MessageSquare, Bell, FileText, Globe, ArrowRight, Star,
  Zap, Lock, RefreshCw, CheckCircle2, Phone, Mail, MapPin, User, Building2
} from "lucide-react";
import DemoVideoPlayer from "@/components/DemoVideoPlayer";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function CountUp({ target, duration = 1800, suffix = "" }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!target) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTime = performance.now();
        const tick = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setValue(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{value.toLocaleString("pt-BR")}{suffix}</span>;
}

const FeatureCard = ({ icon: Icon, title, desc, accent }) => (
  <div className={`group relative bg-[#0d1f1a] border border-[#1e3a30] rounded-2xl p-6 hover:border-[#2d6a4f] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#2d6a4f]/10`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${accent}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: "Manrope" }}>{title}</h3>
    <p className="text-sm text-[#6b9e82] leading-relaxed">{desc}</p>
  </div>
);

const StepCard = ({ number, title, desc }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#2d6a4f] flex items-center justify-center text-white font-bold text-sm">
      {number}
    </div>
    <div>
      <p className="font-bold text-white mb-1" style={{ fontFamily: "Manrope" }}>{title}</p>
      <p className="text-sm text-[#6b9e82] leading-relaxed">{desc}</p>
    </div>
  </div>
);

export default function DemoPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", municipality: "", state: "", phone: "", email: "" });
  const [formStatus, setFormStatus] = useState("idle");
  const formRef = useRef(null);

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.municipality || !form.phone) return;
    setFormStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setFormStatus("success");
    } catch {
      setFormStatus("error");
    }
  }

  const MAIN_STATS = [
    { label: "Análises de IA Simultâneas", value: 78, suffix: "+", icon: Brain, color: "text-violet-400" },
    { label: "Redução no Tempo de Cicatrização", value: 38, suffix: "%", icon: TrendingUp, color: "text-emerald-400" },
    { label: "Economia de Custo por Paciente", value: 24, suffix: "%", icon: Package, color: "text-amber-400" },
  ];

  const FEATURES = [
    { icon: Activity, title: "Registro Clínico Estruturado", desc: "Wizard em 4 etapas guia o profissional no registro padronizado de evoluções com fotos, dimensões, tecidos e exsudato.", accent: "bg-emerald-600" },
    { icon: Brain, title: "IA com Visão Computacional", desc: "Nossa Inteligência Artificial desenvolvida especificamente para feridas crônicas analisa fotos da lesão, classifica risco, identifica tecidos e sugere protocolo de curativo com base em evidências.", accent: "bg-violet-600" },
    { icon: Package, title: "Gestão de Materiais", desc: "Controle de consumo de coberturas por unidade de saúde. Previsão de compras para o próximo mês baseada em 3 meses de histórico.", accent: "bg-amber-600" },
    { icon: BarChart3, title: "Painel Municipal", desc: "Indicadores estratégicos para gestores: distribuição de lesões, carga de trabalho, controle glicêmico de diabéticos, tendências mensais.", accent: "bg-sky-600" },
    { icon: MessageSquare, title: "Interconsulta Especializada", desc: "Profissionais solicitam avaliação de especialista em feridas diretamente pelo sistema, com histórico completo e fotos vinculados.", accent: "bg-rose-600" },
    { icon: Bell, title: "SMS de Medicações e Consultas", desc: "Lembretes diários de medicações e consultas via SMS para pacientes, aumentando a adesão ao tratamento e a eficácia terapêutica.", accent: "bg-orange-600" },
    { icon: Smartphone, title: "PWA — Funciona Offline", desc: "Instalável no celular como app nativo. Profissionais em campo continuam registrando evoluções mesmo sem conexão.", accent: "bg-teal-600" },
    { icon: Shield, title: "Segurança e Auditoria", desc: "Autenticação própria com bcrypt, sessões seguras, proteção contra brute force e log de auditoria completo de cada ação.", accent: "bg-indigo-600" },
    { icon: FileText, title: "Relatório Clínico com Assinatura", desc: "Exportação em PDF com dados clínicos, fotos e assinatura eletrônica do profissional (Lei 14.063/2020).", accent: "bg-green-600" },
  ];

  return (
    <div className="min-h-screen bg-[#07110e] text-white overflow-x-hidden">

      {/* ── NAVBAR ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-[#1a3328] bg-[#07110e]/90 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#2d6a4f] flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white" style={{ fontFamily: "Manrope" }}>RedeCicatriza</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-[#6b9e82] hover:text-white transition-colors"
              data-testid="demo-nav-login"
            >
              Entrar
            </button>
            <button
              onClick={scrollToForm}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#2d6a4f] hover:bg-[#3a8a65] rounded-xl text-sm font-semibold text-white transition-all"
              data-testid="demo-nav-contact"
            >
              Solicitar Demo <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-20 px-4 sm:px-6 overflow-hidden">
        {/* Background grid */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "linear-gradient(#2d6a4f22 1px, transparent 1px), linear-gradient(90deg, #2d6a4f22 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        {/* Glow */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#2d6a4f]/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2d6a4f]/20 border border-[#2d6a4f]/40 rounded-full text-xs font-semibold text-[#4ecb7a] mb-6 animate-fade-in" data-testid="demo-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4ecb7a] animate-pulse" />
            Plataforma Municipal de Feridas Crônicas — Atenção Primária
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight text-white" style={{ fontFamily: "Manrope" }} data-testid="demo-hero-title">
            Cicatrização Inteligente<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4ecb7a] to-[#2d9e6e]">
              para todo o Município
            </span>
          </h1>

          <p className="text-base sm:text-lg text-[#7ab898] max-w-2xl mx-auto mb-10 leading-relaxed">
            O RedeCicatriza conecta equipes de saúde, especialistas e gestores no
            cuidado de úlceras venosas, pé diabético e lesões por pressão —
            com IA clínica, registro fotográfico e painel de gestão em tempo real.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={scrollToForm}
              className="flex items-center gap-2 px-7 py-3.5 bg-[#2d6a4f] hover:bg-[#3a8a65] rounded-2xl font-bold text-white transition-all hover:scale-105 shadow-xl shadow-[#2d6a4f]/30"
              data-testid="demo-cta-primary">
              Quero para meu Município <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/login")}
              className="flex items-center gap-2 px-7 py-3.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-2xl font-semibold text-white transition-all"
              data-testid="demo-cta-login">
              Acessar Sistema
            </button>
          </div>
        </div>
      </section>

      {/* ── STATS COUNTER ── */}
      <section className="py-16 px-4 sm:px-6 border-y border-[#1a3328]" data-testid="demo-stats-section">
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
          {MAIN_STATS.map((s, i) => (
            <div key={i} className="text-center" data-testid={`demo-stat-${i}`}>
              <s.icon className={`w-7 h-7 mx-auto mb-3 ${s.color}`} />
              <p className={`text-4xl sm:text-5xl font-black mb-1 ${s.color}`} style={{ fontFamily: "Manrope" }}>
                <CountUp target={s.value} suffix={s.suffix} />
              </p>
              <p className="text-sm text-[#6b9e82] font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-20 px-4 sm:px-6 bg-[#0a1a14]" data-testid="demo-features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-[#4ecb7a] uppercase tracking-widest mb-3">Funcionalidades</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: "Manrope" }}>
              Tudo que sua equipe precisa,<br className="hidden sm:block" /> em um só lugar
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <FeatureCard key={i} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-[#4ecb7a] uppercase tracking-widest mb-3">Fluxo de Uso</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: "Manrope" }}>Como funciona na prática</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            <StepCard number="1" title="Cadastro do Paciente" desc="Profissional cadastra o paciente com comorbidades, medicações e lesão ativa. O sistema identifica automaticamente fatores de risco." />
            <StepCard number="2" title="Registro da Evolução" desc="Wizard guiado em 4 etapas: dimensões, fotos, análise de IA e prescrição do curativo com materiais utilizados." />
            <StepCard number="3" title="Análise de IA" desc="Nossa Inteligência Artificial, desenvolvida especificamente para este propósito, analisa fotos e dados clínicos, classifica risco, identifica tecidos e sugere protocolo baseado no histórico de curativos." />
            <StepCard number="4" title="Gestão Municipal" desc="Painel com indicadores, previsão de compras de materiais, alertas de casos críticos e relatórios para tomada de decisão da secretaria." />
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE BADGES ── */}
      <section className="py-14 px-4 sm:px-6 border-y border-[#1a3328]">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-xs font-semibold text-[#6b9e82] uppercase tracking-widest mb-8">Conformidade e Tecnologia</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Lock, label: "LGPD Compliance", sub: "Dados protegidos por lei" },
              { icon: FileText, label: "Lei 14.063/2020", sub: "Assinatura eletrônica" },
              { icon: RefreshCw, label: "Offline First", sub: "PWA com Service Worker" },
              { icon: Zap, label: "IA Clínica", sub: "Suporte à decisão estratégica e otimizada" },
            ].map((b, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4 bg-[#0d1f1a] border border-[#1e3a30] rounded-2xl">
                <b.icon className="w-5 h-5 text-[#4ecb7a] mb-2" />
                <p className="text-xs font-bold text-white">{b.label}</p>
                <p className="text-xs text-[#6b9e82] mt-0.5">{b.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEAD FORM ── */}
      <section ref={formRef} className="py-20 px-4 sm:px-6 bg-[#0a1a14]" data-testid="demo-lead-form-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-[#4ecb7a] uppercase tracking-widest mb-3">Demonstração Gratuita</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3" style={{ fontFamily: "Manrope" }}>
              Leve o RedeCicatriza<br />para o seu município
            </h2>
            <p className="text-[#6b9e82] text-sm leading-relaxed">
              Preencha o formulário e entraremos em contato em até 24h para agendar a demonstração.
            </p>
          </div>

          {formStatus === "success" ? (
            <div className="bg-[#0d1f1a] border border-[#2d6a4f] rounded-2xl p-10 text-center" data-testid="lead-form-success">
              <div className="w-14 h-14 rounded-full bg-[#2d6a4f]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#4ecb7a]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "Manrope" }}>Solicitação recebida!</h3>
              <p className="text-[#6b9e82] text-sm">Nossa equipe entrará em contato em breve para agendar a demonstração do RedeCicatriza para o seu município.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#0d1f1a] border border-[#1e3a30] rounded-2xl p-6 sm:p-8 space-y-4" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Nome */}
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-[#6b9e82] uppercase tracking-wide block mb-1.5">
                    Nome Completo *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7060]" />
                    <input
                      type="text"
                      required
                      placeholder="Seu nome"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full bg-[#07110e] border border-[#1e3a30] focus:border-[#2d6a4f] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#3a5a48] outline-none transition-colors"
                      data-testid="lead-input-name"
                    />
                  </div>
                </div>

                {/* Município */}
                <div>
                  <label className="text-xs font-semibold text-[#6b9e82] uppercase tracking-wide block mb-1.5">
                    Município *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7060]" />
                    <input
                      type="text"
                      required
                      placeholder="Nome do município"
                      value={form.municipality}
                      onChange={e => setForm(f => ({ ...f, municipality: e.target.value }))}
                      className="w-full bg-[#07110e] border border-[#1e3a30] focus:border-[#2d6a4f] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#3a5a48] outline-none transition-colors"
                      data-testid="lead-input-municipality"
                    />
                  </div>
                </div>

                {/* Estado */}
                <div>
                  <label className="text-xs font-semibold text-[#6b9e82] uppercase tracking-wide block mb-1.5">
                    Estado
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7060]" />
                    <input
                      type="text"
                      placeholder="UF (ex: SP)"
                      maxLength={2}
                      value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))}
                      className="w-full bg-[#07110e] border border-[#1e3a30] focus:border-[#2d6a4f] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#3a5a48] outline-none transition-colors"
                      data-testid="lead-input-state"
                    />
                  </div>
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="text-xs font-semibold text-[#6b9e82] uppercase tracking-wide block mb-1.5">
                    WhatsApp *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7060]" />
                    <input
                      type="tel"
                      required
                      placeholder="(11) 99999-9999"
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full bg-[#07110e] border border-[#1e3a30] focus:border-[#2d6a4f] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#3a5a48] outline-none transition-colors"
                      data-testid="lead-input-phone"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-semibold text-[#6b9e82] uppercase tracking-wide block mb-1.5">
                    E-mail
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a7060]" />
                    <input
                      type="email"
                      placeholder="seu@email.com"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full bg-[#07110e] border border-[#1e3a30] focus:border-[#2d6a4f] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-[#3a5a48] outline-none transition-colors"
                      data-testid="lead-input-email"
                    />
                  </div>
                </div>
              </div>

              {formStatus === "error" && (
                <p className="text-xs text-red-400 text-center">Algo deu errado. Tente novamente ou entre em contato por email.</p>
              )}

              <button
                type="submit"
                disabled={formStatus === "loading"}
                className="w-full flex items-center justify-center gap-2 py-4 bg-[#2d6a4f] hover:bg-[#3a8a65] disabled:opacity-60 disabled:cursor-not-allowed rounded-2xl font-bold text-white transition-all hover:scale-[1.02] shadow-xl shadow-[#2d6a4f]/20 text-sm mt-2"
                data-testid="lead-form-submit"
              >
                {formStatus === "loading" ? (
                  <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Enviando...</>
                ) : (
                  <><Star className="w-4 h-4" /> Solicitar Demonstração Gratuita</>
                )}
              </button>

              <p className="text-xs text-[#3a5a48] text-center">
                Sem spam. Seus dados são usados apenas para contato sobre o RedeCicatriza.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-24 px-4 sm:px-6 relative overflow-hidden" data-testid="demo-cta-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#07110e] via-[#0d2a1e] to-[#07110e]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#2d6a4f]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#2d6a4f] flex items-center justify-center mx-auto mb-6">
            <Globe className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4" style={{ fontFamily: "Manrope" }}>
            Leve o RedeCicatriza<br />para o seu município
          </h2>
          <p className="text-[#7ab898] mb-8 leading-relaxed">
            Sistema desenvolvido especificamente para a realidade da atenção primária brasileira.
            Configuração personalizada para seu município, treinamento da equipe e suporte contínuo.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:contato@redecicatriza.com?subject=Solicito demonstração do RedeCicatriza&body=Olá, represento o município de ___ e gostaria de saber mais sobre o RedeCicatriza."
              className="flex items-center justify-center gap-2 px-8 py-4 bg-[#2d6a4f] hover:bg-[#3a8a65] rounded-2xl font-bold text-white transition-all hover:scale-105 shadow-2xl shadow-[#2d6a4f]/30 text-sm"
              data-testid="demo-cta-email"
            >
              <Star className="w-4 h-4" /> Solicitar Demonstração Gratuita
            </a>
            <button
              onClick={() => navigate("/login")}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 rounded-2xl font-semibold text-white transition-all text-sm"
              data-testid="demo-cta-access"
            >
              Acessar o Sistema <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-8 px-4 sm:px-6 border-t border-[#1a3328]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-[#2d6a4f] flex items-center justify-center">
              <Activity className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-bold text-white" style={{ fontFamily: "Manrope" }}>RedeCicatriza</span>
          </div>
          <p className="text-xs text-[#4a7060] text-center">
            Plataforma de acompanhamento de feridas crônicas para atenção primária • Brasil
          </p>
          <div className="flex items-center gap-3 text-xs text-[#4a7060]">
            <button onClick={() => navigate("/login")} className="hover:text-[#6b9e82] transition-colors">Login</button>
            <span>·</span>
            <a href="mailto:contato@redecicatriza.com" className="hover:text-[#6b9e82] transition-colors">Contato</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
